import type { Spread } from "lexical";

import {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from "lexical";
import { Trigger } from "./type";

export const SUGGESTION = "suggestion";
export const SUGGESTION_HTML_ATTRIBUTE = `data-lexical-${SUGGESTION}`;
export type SuggestionType = typeof SUGGESTION;

export type SerializedSuggestionNode = Spread<
  {
    suggestionName: string;
    prefixChar: Trigger;
    type: SuggestionType;
    version: 1;
  },
  SerializedTextNode
>;

function convertSuggestionElement(
  domNode: HTMLElement,
): DOMConversionOutput | null {
  const textContent = domNode.textContent;

  if (textContent !== null) {
    const prefixChar = textContent.charAt(0) as Trigger satisfies Trigger;
    const suggestionName = textContent.substring(1);
    const node = $createSuggestionNode(suggestionName, prefixChar);
    return {
      node,
    };
  }

  return null;
}

export class SuggestionNode extends TextNode {
  __suggestion: string;
  __prefixChar: Trigger;

  static getType(): SuggestionType {
    return SUGGESTION;
  }

  static clone(node: SuggestionNode): SuggestionNode {
    return new SuggestionNode(
      node.__suggestion,
      node.__prefixChar,
      node.__text,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedSuggestionNode): SuggestionNode {
    const node = $createSuggestionNode(
      serializedNode.suggestionName,
      serializedNode.prefixChar,
    );
    node.setTextContent(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  constructor(
    suggestionName: string,
    prefixChar: Trigger,
    text?: string,
    key?: NodeKey,
  ) {
    super(text ?? prefixChar + suggestionName, key);
    this.__suggestion = suggestionName;
    this.__prefixChar = prefixChar;
  }

  exportJSON(): SerializedSuggestionNode {
    return {
      ...super.exportJSON(),
      suggestionName: this.__suggestion,
      prefixChar: this.__prefixChar,
      type: SUGGESTION,
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute(SUGGESTION_HTML_ATTRIBUTE, "true");
    element.textContent = this.__text;
    return { element };
  }

  exportMarkdown(): string {
    return `${this.__prefixChar}${this.__suggestion}`;
  }

  isSegmented(): false {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute(SUGGESTION_HTML_ATTRIBUTE)) {
          return null;
        }
        return {
          conversion: convertSuggestionElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }

  isToken(): true {
    return true;
  }
}

export function $createSuggestionNode(
  suggestionName: string,
  prefixChar: Trigger,
): SuggestionNode {
  const suggestionNode = new SuggestionNode(suggestionName, prefixChar);
  suggestionNode.setMode("segmented").toggleDirectionless();
  return suggestionNode;
}

export function $isSuggestionNode(
  node: LexicalNode | null | undefined,
): node is SuggestionNode {
  return node instanceof SuggestionNode;
}
